import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { CheckCircle2, ChevronRight, ShoppingCart, User, Mail, Phone, MapPin, Calendar as CalendarIcon, Clock, CreditCard, ChevronDown, Trash2, Plus, Minus, AlertCircle, ArrowLeft, Loader2, Store as StoreIcon, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import api from "@/services/api";
import { toast } from "sonner";
import { useStore } from "@/hooks/useStore";
import { useCustomer } from "@/context/CustomerContext";
import { StoreHeader, StoreFooter } from "@/components/store";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { z } from "zod";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import StripePayment from "@/components/store/StripePayment";
import { US_STATES, CA_PROVINCES, COUNTRIES } from "@/utils/locationData";
import { storePath, getSubdomain } from "../utils/storeUtils";
import { getImageUrl } from "../utils/imageUtils";

const step1Schema = z.object({
    contact_email: z.string().min(1, "Email is required").email("Invalid email format"),
    shipping_address: z.object({
        firstname: z.string().min(1, "First name is required"),
        lastname: z.string().min(1, "Last name is required"),
        phone: z.string().min(10, "Phone number must be at least 10 digits"),
        address: z.string().min(1, "Address is required"),
        city: z.string().min(1, "City is required"),
        state: z.string().min(1, "State is required"),
        zipcode: z.string().min(1, "Zipcode is required"),
    }),
});

const step2Schema = z.object({
    // delivery_date: z.string().min(1, "Delivery date is required"),
    // delivery_time: z.string().min(1, "Delivery time is required"),
});


export default function Checkout() {
    const { subdomain: paramSubdomain } = useParams();
    const subdomain = paramSubdomain || getSubdomain();
    const navigate = useNavigate();
    const location = useLocation();
    const { cart, cartSubtotal, removeFromCart, updateQuantity, clearCart, setCartOpen } = useCart();
    const [isProcessing, setIsProcessing] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [resellerUser, setResellerUser] = useState(null);
    const [errors, setErrors] = useState({});
    const [clientSecret, setClientSecret] = useState("");
    const [stripePromise, setStripePromise] = useState(null);

    // Form State
    const [shippingMethods, setShippingMethods] = useState([]);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [shippingCost, setShippingCost] = useState(0);

    // Form State
    const [formData, setFormData] = useState({
        deceased_name: "",
        contact_email: "",
        shipping_address: {
            firstname: "",
            lastname: "",
            phone: "",
            address: "",
            city: "",
            country: "US",
            state: "",
            zipcode: ""
        },
        is_guest: true
    });

    // Use custom hook for store data
    const { store, theme, storeAppearance, platformInfo, loading } = useStore(subdomain);
    const { checkExists, customer, smartLoginData, updateAddresses } = useCustomer(); // Customer Context
    const [saveAddress, setSaveAddress] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    // Detect Context
    const isFuneralStore = location.pathname.includes('/reseller/funeral-store');

    // Effect: Initialize Shipping Methods from Store Config
    useEffect(() => {
        if (store?.shipping_config?.shipping_types) {
            const activeTypes = store.shipping_config.shipping_types.filter(t => t.is_active);
            setShippingMethods(activeTypes);
            // Select first option by default
            if (activeTypes.length > 0 && !selectedShipping) {
                handleShippingSelect(activeTypes[0]);
            }
        }
    }, [store, cartSubtotal]);

    // Initialize Stripe as soon as store data is available
    useEffect(() => {
        if (store?.stripe_publishable_key && !stripePromise) {
            setStripePromise(loadStripe(store.stripe_publishable_key));
        }
    }, [store]);

    // Recalculate shipping cost when options or subtotal changes
    useEffect(() => {
        if (selectedShipping) {
            const cost = calculateShippingCost(selectedShipping, cartSubtotal);
            setShippingCost(cost);
        }
    }, [selectedShipping, cartSubtotal, store]);

    const calculateShippingCost = (method, subtotal) => {
        if (!method) return 0;

        // Check Free Shipping Threshold
        const threshold = store?.shipping_config?.free_shipping_threshold;
        if (threshold && subtotal >= threshold) {
            return 0;
        }

        // Find matching tier
        const tier = method.tiers?.find(t => subtotal >= t.min_value && subtotal < t.max_value);
        if (tier) {
            return tier.shipping_cost;
        }

        // Fallback: If above highest tier, use the highest tier cost (or 0?) - Usually highest tier covers up to infinity or we take the last one
        // If simply not found (e.g. cart 0), return 0 or min
        return method.tiers?.[method.tiers.length - 1]?.shipping_cost || 0;
    };

    const handleShippingSelect = (method) => {
        setSelectedShipping(method);
        const cost = calculateShippingCost(method, cartSubtotal);
        setShippingCost(cost);
    };

    // ... (Existing Effects)

    // Handle Auto-fill from Logged In Customer
    useEffect(() => {
        if (customer) {
            const defaultAddress = customer.addresses?.find(a => a.is_default) || customer.addresses?.[0] || null;

            setFormData(prev => {
                // Prevent infinite loop if data constitutes no actual change
                const newEmail = customer.email || prev.contact_email;
                if (prev.contact_email === newEmail && prev.shipping_address.address === defaultAddress?.address) {
                    return prev;
                }

                return {
                    ...prev,
                    contact_email: newEmail,
                    is_guest: customer.is_guest ?? true,
                    shipping_address: {
                        ...prev.shipping_address,
                        firstname: defaultAddress?.firstname || (customer.name ? customer.name.split(' ')[0] : prev.shipping_address.firstname),
                        lastname: defaultAddress?.lastname || (customer.name ? customer.name.split(' ').slice(1).join(' ') : prev.shipping_address.lastname),
                        phone: defaultAddress?.phone || customer.phone || prev.shipping_address.phone,
                        address: defaultAddress?.address || prev.shipping_address.address,
                        city: defaultAddress?.city || prev.shipping_address.city,
                        state: defaultAddress?.state || prev.shipping_address.state,
                        zipcode: defaultAddress?.zipcode || prev.shipping_address.zipcode,
                        country: defaultAddress?.country || prev.shipping_address.country,
                    }
                };
            });
        }
    }, [customer]);

    // Fetch Reseller User Info if in Funeral Store
    useEffect(() => {
        if (isFuneralStore) {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                try {
                    const user = JSON.parse(userStr);
                    setResellerUser(user);
                } catch (e) {
                    console.error("Failed to parse user from localStorage", e);
                }
            }
        }
    }, [isFuneralStore]);
    // Prefill for Funeral Store (Reseller Address)
    useEffect(() => {
        const fetchResellerAddress = async () => {
            if (isFuneralStore) {
                try {
                    const response = await api.get('/reseller/profile/address');
                    const addr = response.data;
                    setFormData(prev => ({
                        ...prev,
                        contact_email: prev.contact_email || addr.email || (customer?.email || ''),
                        shipping_address: {
                            ...prev.shipping_address,
                            firstname: addr.business_name || '',
                            lastname: 'Funeral Home',
                            phone: addr.phone || '',
                            address: addr.business_address?.address || addr.business_address || '',
                            city: addr.business_address?.city || '',
                            state: addr.business_address?.state || '',
                            zipcode: addr.business_address?.zipcode || '',
                            country: addr.business_address?.country || 'US',
                        }
                    }));
                } catch (error) {
                    console.error("Failed to fetch reseller address", error);
                }
            }
        };
        fetchResellerAddress();
    }, [isFuneralStore, customer]);

    // Prefill shipping address from store's business address for guest checkout (Public Store Only)
    useEffect(() => {
        if (!isFuneralStore && store?.business_address && !customer) {
            const ba = store.business_address;
            // Only prefill if the store has address data and user hasn't typed anything yet
            setFormData(prev => {
                // Skip if user already has address data filled in
                if (prev.shipping_address.address || prev.shipping_address.city) return prev;
                return {
                    ...prev,
                    shipping_address: {
                        ...prev.shipping_address,
                        address: ba.address || prev.shipping_address.address,
                        city: ba.city || prev.shipping_address.city,
                        state: ba.state || prev.shipping_address.state,
                        zipcode: ba.zipcode || prev.shipping_address.zipcode,
                        country: ba.country || prev.shipping_address.country,
                    }
                };
            });
        }
    }, [store, customer]);

    // ...

    const validateStep = (step) => {
        setErrors({});
        try {
            if (step === 1) {
                step1Schema.parse(formData);
                return true;
            } else if (step === 2) {
                if (!selectedShipping) {
                    setErrors({ selectedShipping: "Please select a shipping method" });
                    toast.error("Please select a shipping method");
                    return false;
                }
                step2Schema.parse(formData);
                return true;
            } else if (step === 3) {
                // Final validation of all previous steps
                step1Schema.parse(formData);
                if (!selectedShipping) {
                    setErrors({ selectedShipping: "Please select a shipping method" });
                    return false;
                }
                step2Schema.parse(formData);
                return true;
            }
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                const newErrors = {};
                error.errors.forEach((err) => {
                    const path = err.path.join(".");
                    newErrors[path] = err.message;
                });
                setErrors(newErrors);
                toast.error("Please correct the errors on the page");
            }
            return false;
        }
    };

    const initiatePayment = async () => {
        setIsProcessing(true);
        try {
            const payload = {
                store_subdomain: subdomain || null,
                items: cart.map(item => ({
                    product_id: item.product_id,
                    product_type: item.product_type || 'main',
                    name: item.name,
                    quantity: item.quantity,
                    price: item.total_price || item.base_price,
                    base_price: item.base_price,
                    image_url: item.image_url,
                    customizations: item.customizations,
                    // Product Management v3 — sales channel for per-channel commission.
                    channel: item.channel || 'edisplay'
                })),
                shipping_cost: shippingCost,
                shipping_method: selectedShipping?.name,
                shipping_method_id: selectedShipping?._id,
                order_type: isFuneralStore ? 'funeral_purchase' : 'regular',
                customer_id: isFuneralStore ? resellerUser?.user_id : (customer?._id || null),
                ...formData
            };

            const response = await api.post('/checkout/create-payment-intent', payload);

            if (response.data.clientSecret) {
                setClientSecret(response.data.clientSecret);
                setActiveStep(3);
            } else {
                throw new Error("Failed to initialize payment");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to initialize payment. Please try again.");
            console.error("Init payment error:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleBlur = async (e) => {
        // Smart Lookup disabled for privacy. Auto-fill only happens for authenticated users.
    };

    const handleInputChange = (e, section = null) => {
        const { name, value } = e.target;
        const fieldPath = section ? `${section}.${name}` : name;

        // Clear error for this field
        if (errors[fieldPath]) {
            setErrors(prev => {
                const newErrs = { ...prev };
                delete newErrs[fieldPath];
                return newErrs;
            });
        }

        if (section) {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [name]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSelectChange = (value, name, section = null) => {
        const fieldPath = section ? `${section}.${name}` : name;

        // Clear error for this field
        if (errors[fieldPath]) {
            setErrors(prev => {
                const newErrs = { ...prev };
                delete newErrs[fieldPath];
                return newErrs;
            });
        }

        if (section) {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [name]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const calculateTotal = () => {
        return cartSubtotal + shippingCost;
    };

    if (subdomain && loading) {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                <div className="h-16 border-b flex items-center px-6 gap-4">
                    <div className="w-10 h-10 rounded-lg animate-shimmer" />
                    <div className="w-32 h-6 rounded animate-shimmer" />
                </div>
                <div className="flex-1 max-w-7xl mx-auto w-full p-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8 space-y-8">
                        <div className="h-12 w-1/4 rounded animate-shimmer" />
                        <div className="h-64 rounded-2xl animate-shimmer" />
                        <div className="h-64 rounded-2xl animate-shimmer" />
                    </div>
                    <div className="lg:col-span-4 space-y-6">
                        <div className="h-12 w-1/2 rounded animate-shimmer" />
                        <div className="h-80 rounded-2xl animate-shimmer" />
                    </div>
                </div>
            </div>
        );
    }

    if (cart.length === 0) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 animate-fade-in">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 text-slate-400">
                    <ShoppingCart size={32} />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Your cart is empty</h2>
                <p className="text-slate-600 mb-6 text-center max-w-xs">Looks like you haven't added anything to your cart yet.</p>
                <Button
                    onClick={() => navigate(storePath(subdomain, "/") || '/products')}
                    className="bg-indigo-600 hover:bg-indigo-700 rounded-full"
                >
                    Return to Shop
                </Button>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            {!subdomain && !isFuneralStore && <Header />}

            <div className={cn("flex-1 pb-20", isFuneralStore ? "pt-8" : "")}>
                {/* Breadcrumbs - Exclude for Funeral Store */}
                {!isFuneralStore && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            <Link to="/" className="hover:text-indigo-600">Home</Link>
                            <ChevronRight size={10} />
                            {subdomain ? (
                                <Link to={storePath(subdomain, "/")} className="hover:text-indigo-600">Shop</Link>
                            ) : (
                                <Link to="/products" className="hover:text-indigo-600">Shop</Link>
                            )}
                            <ChevronRight size={10} />
                            <span className="text-slate-900">Check Out</span>
                        </div>
                    </div>
                )}

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                        {/* Main Info Columns */}
                        <div className="lg:col-span-8 space-y-8">

                            {/* Step 1: Delivery Address */}
                            <div className="card-elevated p-0 overflow-hidden rounded-3xl border-slate-100">
                                <div className={cn(
                                    "flex items-center gap-4 p-6 border-b transition-colors",
                                    activeStep === 1 ? "bg-white border-slate-100" : "bg-slate-50 border-transparent text-slate-500"
                                )}>
                                    <div className={cn(
                                        "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm",
                                        activeStep === 1 ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200" : "bg-slate-200 text-slate-500"
                                    )}>1</div>
                                    <h3 className="font-bold text-lg">Delivery Address</h3>
                                </div>

                                {activeStep === 1 && (
                                    <div className="p-8 space-y-8 animate-in fade-in slide-in-from-top-4 duration-300">
                                        {/* Deceased Details - Hide for Funeral Store */}
                                        {!isFuneralStore && (
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="font-bold text-slate-900 italic" style={{ fontSize: '1.25rem' }}>
                                                        Deseased Details
                                                    </h4>
                                                    {!customer && (
                                                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                                                            {/* Already have an account? <Link to={storePath(subdomain, '/login')} className="text-indigo-600 font-bold hover:underline">Login</Link> */}
                                                            Already have an account? <Link to="/login" className="text-indigo-600 font-bold hover:underline">Login</Link>
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Deceased Name</Label>
                                                    <Input
                                                        name="deceased_name"
                                                        value={formData.deceased_name}
                                                        onChange={handleInputChange}
                                                        placeholder="Full name of deceased"
                                                        className="h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Contact Information */}
                                        <div className="space-y-4 pt-4 border-t border-slate-50">
                                            <h4 className="font-bold text-slate-900">Contact Information</h4>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email Address*</Label>
                                                <Input
                                                    name="contact_email"
                                                    type="email"
                                                    value={formData.contact_email}
                                                    onChange={handleInputChange}
                                                    onBlur={handleBlur}
                                                    placeholder="you@example.com"
                                                    className={cn(
                                                        "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                        errors.contact_email && "border-red-500 bg-white"
                                                    )}
                                                />
                                                {errors.contact_email && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.contact_email}</p>}
                                            </div>
                                        </div>

                                        {/* Shipping Details */}
                                        <div className="space-y-4 pt-4 border-t border-slate-50">
                                            <h4 className="font-bold text-slate-900">Shipping Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">First Name*</Label>
                                                    <Input
                                                        name="firstname"
                                                        value={formData.shipping_address.firstname}
                                                        onChange={(e) => handleInputChange(e, "shipping_address")}
                                                        className={cn(
                                                            "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                            errors['shipping_address.firstname'] && "border-red-500 bg-white"
                                                        )}
                                                    />
                                                    {errors['shipping_address.firstname'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.firstname']}</p>}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Last Name*</Label>
                                                    <Input
                                                        name="lastname"
                                                        value={formData.shipping_address.lastname}
                                                        onChange={(e) => handleInputChange(e, "shipping_address")}
                                                        className={cn(
                                                            "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                            errors['shipping_address.lastname'] && "border-red-500 bg-white"
                                                        )}
                                                    />
                                                    {errors['shipping_address.lastname'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.lastname']}</p>}
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Phone*</Label>
                                                <Input
                                                    name="phone"
                                                    value={formData.shipping_address.phone}
                                                    onChange={(e) => handleInputChange(e, "shipping_address")}
                                                    onBlur={handleBlur}
                                                    className={cn(
                                                        "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                        errors['shipping_address.phone'] && "border-red-500 bg-white"
                                                    )}
                                                />
                                                {errors['shipping_address.phone'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.phone']}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Address*</Label>
                                                <Input
                                                    name="address"
                                                    value={formData.shipping_address.address}
                                                    onChange={(e) => handleInputChange(e, "shipping_address")}
                                                    className={cn(
                                                        "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                        errors['shipping_address.address'] && "border-red-500 bg-white"
                                                    )}
                                                />
                                                {errors['shipping_address.address'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.address']}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Town / City*</Label>
                                                <Input
                                                    name="city"
                                                    value={formData.shipping_address.city}
                                                    onChange={(e) => handleInputChange(e, "shipping_address")}
                                                    className={cn(
                                                        "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                        errors['shipping_address.city'] && "border-red-500 bg-white"
                                                    )}
                                                />
                                                {errors['shipping_address.city'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.city']}</p>}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Country*</Label>
                                                    <Select
                                                        value={formData.shipping_address.country}
                                                        onValueChange={(val) => {
                                                            handleSelectChange(val, "country", "shipping_address");
                                                            // Reset state when country changes
                                                            handleSelectChange("", "state", "shipping_address");
                                                        }}
                                                    >
                                                        <SelectTrigger className={cn(
                                                            "h-12 bg-slate-50 border-transparent rounded-xl",
                                                            errors['shipping_address.country'] && "border-red-500 bg-white"
                                                        )}>
                                                            <SelectValue placeholder="Select Country" />
                                                        </SelectTrigger>
                                                        {errors['shipping_address.country'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.country']}</p>}
                                                        <SelectContent>
                                                            {COUNTRIES.map(c => (
                                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">State*</Label>
                                                    <Popover open={stateDropdownOpen} onOpenChange={setStateDropdownOpen}>
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                variant="outline"
                                                                role="combobox"
                                                                aria-expanded={stateDropdownOpen}
                                                                className={cn(
                                                                    "w-full justify-between h-12 bg-slate-50 border-transparent rounded-xl font-normal hover:bg-slate-100",
                                                                    !formData.shipping_address.state && "text-muted-foreground",
                                                                    errors['shipping_address.state'] && "border-red-500 bg-white"
                                                                )}
                                                            >
                                                                {formData.shipping_address.state
                                                                    ? [...US_STATES, ...CA_PROVINCES].find((s) => s.value === formData.shipping_address.state)?.label
                                                                    : formData.shipping_address.country === 'CA' ? "Choose a Province" : "Choose a State"}
                                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-[300px] p-0" align="start">
                                                            <Command>
                                                                <CommandInput placeholder={formData.shipping_address.country === 'CA' ? "Search province..." : "Search state..."} className="h-9" />
                                                                <CommandEmpty>{formData.shipping_address.country === 'CA' ? "No province found." : "No state found."}</CommandEmpty>
                                                                <CommandList>
                                                                    <CommandGroup>
                                                                        {(formData.shipping_address.country === 'CA' ? CA_PROVINCES : US_STATES).map((state) => (
                                                                            <CommandItem
                                                                                key={state.value}
                                                                                value={state.label}
                                                                                onSelect={() => {
                                                                                    handleSelectChange(state.value, "state", "shipping_address");
                                                                                    setStateDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                {state.label}
                                                                                <Check
                                                                                    className={cn(
                                                                                        "ml-auto h-4 w-4",
                                                                                        formData.shipping_address.state === state.value ? "opacity-100" : "opacity-0"
                                                                                    )}
                                                                                />
                                                                            </CommandItem>
                                                                        ))}
                                                                    </CommandGroup>
                                                                </CommandList>
                                                            </Command>
                                                        </PopoverContent>
                                                    </Popover>
                                                    {errors['shipping_address.state'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.state']}</p>}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Zipcode*</Label>
                                                    <Input
                                                        name="zipcode"
                                                        value={formData.shipping_address.zipcode}
                                                        onChange={(e) => handleInputChange(e, "shipping_address")}
                                                        className={cn(
                                                            "h-12 bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 transition-all rounded-xl",
                                                            errors['shipping_address.zipcode'] && "border-red-500 bg-white"
                                                        )}
                                                    />
                                                    {errors['shipping_address.zipcode'] && <p className="text-[10px] text-red-500 font-bold mt-1">{errors['shipping_address.zipcode']}</p>}
                                                </div>
                                            </div>
                                        </div>

                                        {customer && (
                                            <div className="flex items-center space-x-2 pt-4 pl-1">
                                                <Checkbox
                                                    id="save-address"
                                                    checked={saveAddress}
                                                    onCheckedChange={(checked) => setSaveAddress(checked)}
                                                />
                                                <Label htmlFor="save-address" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-slate-700">
                                                    Save this address to my profile
                                                </Label>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-6">
                                            <button className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-indigo-600 transition-colors">
                                                <ChevronRight className="rotate-180" size={12} />
                                                Continue Shopping
                                            </button>
                                            <Button
                                                onClick={() => validateStep(1) && setActiveStep(2)}
                                                className="bg-indigo-900 hover:bg-black text-white px-8 h-12 rounded-xl"
                                            >
                                                Proceed
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Step 2: Delivery Options */}
                            <div className="card-elevated p-0 overflow-hidden rounded-3xl border-slate-100">
                                <div className={cn(
                                    "flex items-center gap-4 p-6 border-b transition-colors",
                                    activeStep === 2 ? "bg-white border-slate-100" : "bg-slate-50 border-transparent text-slate-500"
                                )}>
                                    <div className={cn(
                                        "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm",
                                        activeStep === 2 ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200" : "bg-slate-200 text-slate-500"
                                    )}>2</div>
                                    <h3 className="font-bold text-lg">Delivery Options</h3>
                                </div>

                                {activeStep === 2 && (
                                    <div className="p-8 space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">

                                        {/* Shipping Methods Radio Group */}
                                        <div className="space-y-4">
                                            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Select Shipping Method</Label>
                                            <RadioGroup
                                                value={selectedShipping?._id}
                                                onValueChange={(val) => {
                                                    const method = shippingMethods.find(m => m._id === val);
                                                    handleShippingSelect(method);
                                                }}
                                                className="space-y-3"
                                            >
                                                {shippingMethods.map((method) => {
                                                    const cost = calculateShippingCost(method, cartSubtotal);
                                                    const isFree = cost === 0;

                                                    return (
                                                        <div key={method._id} className={cn(
                                                            "flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer",
                                                            selectedShipping?._id === method._id
                                                                ? "border-indigo-600 bg-indigo-50/30 ring-1 ring-indigo-600"
                                                                : "border-slate-200 hover:border-indigo-300"
                                                        )}>
                                                            <div className="flex items-center gap-3">
                                                                <RadioGroupItem value={method._id} id={method._id} />
                                                                <div>
                                                                    <Label htmlFor={method._id} className="font-bold text-slate-900 cursor-pointer">{method.name}</Label>
                                                                    {/* <p className="text-xs text-slate-500">Standard delivery time</p> */}
                                                                </div>
                                                            </div>
                                                            <div className="font-bold text-slate-900">
                                                                {isFree ? (
                                                                    <span className="text-green-600">Free</span>
                                                                ) : (
                                                                    `$${cost.toFixed(2)}`
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {shippingMethods.length === 0 && (
                                                    <p className="text-sm text-slate-500 italic">No shipping methods available for this location.</p>
                                                )}
                                            </RadioGroup>
                                            {errors.selectedShipping && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.selectedShipping}</p>}
                                        </div>

                                        <div className="flex items-center justify-between pt-6">
                                            <button
                                                onClick={() => setActiveStep(1)}
                                                className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-indigo-600 transition-colors"
                                            >
                                                <ChevronRight className="rotate-180" size={12} />
                                                Back to Address
                                            </button>
                                            <Button
                                                onClick={() => validateStep(2) && initiatePayment()}
                                                disabled={!selectedShipping || isProcessing}
                                                className="bg-indigo-900 hover:bg-black text-white px-8 h-12 rounded-xl"
                                            >
                                                {isProcessing ? "Loading Payment..." : "Next"}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Step 3: Payment */}
                            <div className="card-elevated p-0 overflow-hidden rounded-3xl border-slate-100">
                                <div className={cn(
                                    "flex items-center gap-4 p-6 transition-colors",
                                    activeStep === 3 ? "bg-white border-b border-slate-100" : "bg-slate-50 text-slate-500"
                                )}>
                                    <div className={cn(
                                        "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm",
                                        activeStep === 3 ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200" : "bg-slate-200 text-slate-500"
                                    )}>3</div>
                                    <h3 className="font-bold text-lg">Pay with <span className="text-indigo-600">Stripe</span></h3>
                                </div>

                                {activeStep === 3 && (
                                    <div className="p-8 animate-in fade-in slide-in-from-top-4 duration-300">
                                        {!store?.stripe_publishable_key ? (
                                            <div className="flex flex-col items-center justify-center py-12 space-y-4">
                                                <AlertCircle className="text-red-500" size={32} />
                                                <p className="text-red-600 font-medium text-center">Payment is not configured for this store. Please contact the store owner.</p>
                                            </div>
                                        ) : clientSecret && stripePromise ? (
                                            <Elements stripe={stripePromise} options={{ clientSecret }}>
                                                <StripePayment
                                                    clientSecret={clientSecret}
                                                    formData={formData}
                                                    totalAmount={calculateTotal()}
                                                    onSuccess={(paymentIntent) => {
                                                        clearCart();
                                                        if (isFuneralStore) {
                                                            navigate(`/reseller/my-purchases?payment_intent=${paymentIntent.id}`);
                                                            toast.success("Order placed successfully!");
                                                        } else {
                                                            const successUrl = subdomain
                                                                ? storePath(subdomain, `/checkout/success?payment_intent=${paymentIntent.id}`)
                                                                : `/checkout/success?payment_intent=${paymentIntent.id}`;
                                                            navigate(successUrl);
                                                        }
                                                    }}
                                                />
                                            </Elements>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-12 space-y-4">
                                                <Loader2 className="animate-spin text-indigo-600" size={32} />
                                                <p className="text-slate-500 font-medium">Initializing secure payment...</p>
                                            </div>
                                        )}

                                        <button
                                            onClick={() => setActiveStep(2)}
                                            className="mt-6 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-indigo-600 transition-colors"
                                        >
                                            <ChevronRight className="rotate-180" size={12} />
                                            Back to Delivery
                                        </button>
                                    </div>
                                )}
                            </div>

                        </div>

                        {/* Sidebar: Your Order */}
                        <div className="lg:col-span-4">
                            <div className="sticky top-8 space-y-6">
                                <h2 className="text-xl font-bold text-slate-900">Your Order</h2>

                                <div className="card-elevated p-6 rounded-3xl border-slate-50 space-y-6">
                                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                        {cart.map((item) => (
                                            <div key={item.uniqueKey} className="flex gap-4 group">
                                                <div className="w-16 h-16 bg-slate-50 rounded-xl overflow-hidden flex-shrink-0 border border-slate-100">
                                                    <img
                                                        src={getImageUrl(item.image_url)}
                                                        alt={item.name}
                                                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-xs font-bold text-slate-900 truncate leading-tight">{item.name}</h4>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] text-slate-400 font-medium">Qty: {item.quantity}</span>
                                                        <span className="text-[10px] text-slate-400 font-medium">•</span>
                                                        <span className="text-[10px] font-bold text-indigo-600">${(item.total_price || item.base_price).toFixed(2)}</span>
                                                    </div>
                                                    {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                        <div className="mt-1 space-y-1 border-l border-slate-100 pl-2">
                                                            {Array.isArray(item.customizations) ? (
                                                                item.customizations.map((cust) => (
                                                                    <div key={cust.id} className="text-[9px]">
                                                                        <div className="flex justify-between font-bold text-slate-500">
                                                                            <span>{cust.name}</span>
                                                                            <span className="text-indigo-600">+${cust.total.toFixed(2)}</span>
                                                                        </div>
                                                                        {cust.fields.map((f, i) => (
                                                                            <p key={i} className="text-slate-400 italic truncate">
                                                                                {f.label}: {f.value}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                Object.entries(item.customizations).map(([label, val]) => (
                                                                    <p key={label} className="text-[9px] text-slate-400 italic truncate">
                                                                        <span className="font-semibold">{label}:</span> {val}
                                                                    </p>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(item.uniqueKey)}
                                                    className="text-slate-300 hover:text-red-500 transition-colors h-fit p-1"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="space-y-3 pt-6 border-t border-slate-100">
                                        <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-400">
                                            <span>Subtotal</span>
                                            <span className="text-slate-900">${cartSubtotal.toFixed(2)}</span>
                                        </div>
                                        {shippingCost > 0 ? (
                                            <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-400">
                                                <span>Shipping ({selectedShipping?.name})</span>
                                                <span className="text-slate-900">${shippingCost.toFixed(2)}</span>
                                            </div>
                                        ) : selectedShipping ? (
                                            <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-400">
                                                <span>Shipping</span>
                                                <span className="text-green-600">Free</span>
                                            </div>
                                        ) : null}

                                        <div className="flex justify-between items-center pt-4 mt-2 border-t border-slate-200">
                                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-red-600">Total Payable</span>
                                            <span className="text-xl font-black text-slate-900">${calculateTotal().toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Secure Checkout Notice */}
                                <div className="flex items-center gap-3 p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                                    <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg shadow-indigo-100">
                                        <CheckCircle2 size={20} />
                                    </div>
                                    <div>
                                        <h5 className="text-[10px] font-black uppercase tracking-widest text-indigo-900">Secure Checkout</h5>
                                        <p className="text-[9px] text-indigo-600/70 font-medium">Your data is always protected by Stripe</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            {!subdomain && !isFuneralStore && <Footer />}
        </div >
    );
}

